import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Loader2, Plus, Edit, Trash2 } from 'lucide-react';
import { AdminPageShell, EmptyRow } from '../components/AdminShared';

export default function AdminFeeSchedulesPage() {
  const [searchParams] = useSearchParams();
  const filterOrgId = searchParams.get('org_id') || '';

  const [schedules, setSchedules] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      org_id: '', name: '', tax_enabled: false, tax_label: 'Tax', tax_rate_percent: 0,
      ai_rate_cents_per_minute: 50, ai_rate_label: 'AI Interpreter IO Rate',
      human_tier1_rate_cents: 150, human_tier1_label: 'Tier 1', human_tier1_languages: [],
      human_tier2_rate_cents: 200, human_tier2_label: 'Tier 2', human_tier2_languages: [],
      human_tier3_rate_cents: 300, human_tier3_label: 'Tier 3', human_tier3_languages: [],
    };
  }

  const load = useCallback(async () => {
    try {
      const [sRes, oRes, lRes] = await Promise.all([
        adminAPI.listFeeSchedules(filterOrgId ? { org_id: filterOrgId } : {}),
        adminAPI.listOrgs(),
        adminAPI.listLanguages()
      ]);
      setSchedules(sRes.data);
      setOrgs(oRes.data.sort((a, b) => a.name.localeCompare(b.name)));
      setLanguages(lRes.data.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterOrgId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditId(null); setForm(defaultForm()); setShowDialog(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    setForm({
      org_id: s.org_id || '',
      name: s.name, tax_enabled: s.tax_enabled, tax_label: s.tax_label || 'Tax',
      tax_rate_percent: s.tax_rate_percent || 0, ai_rate_cents_per_minute: s.ai_rate_cents_per_minute,
      ai_rate_label: s.ai_rate_label || 'AI Interpreter IO Rate',
      human_tier1_rate_cents: s.human_tier1_rate_cents, human_tier1_label: s.human_tier1_label || 'Tier 1',
      human_tier1_languages: s.human_tier1_languages || [],
      human_tier2_rate_cents: s.human_tier2_rate_cents, human_tier2_label: s.human_tier2_label || 'Tier 2',
      human_tier2_languages: s.human_tier2_languages || [],
      human_tier3_rate_cents: s.human_tier3_rate_cents, human_tier3_label: s.human_tier3_label || 'Tier 3',
      human_tier3_languages: s.human_tier3_languages || [],
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (editId) await adminAPI.updateFeeSchedule(editId, form);
      else await adminAPI.createFeeSchedule(form);
      setShowDialog(false); load();
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fee schedule?')) return;
    try { await adminAPI.deleteFeeSchedule(id); load(); } catch (e) { console.error(e); }
  };

  const toggleLang = (tier, code) => {
    const key = `human_tier${tier}_languages`;
    setForm(p => ({ ...p, [key]: p[key].includes(code) ? p[key].filter(l => l !== code) : [...p[key], code] }));
  };

  const orgName = (id) => orgs.find(o => o.id === id)?.name || id || '-';
  const langName = (code) => languages.find(l => l.code === code)?.name || code;

  return (
    <AdminPageShell
      loading={loading}
      title="Fee Schedules"
      subtitle="Set billing rates for organizations with configurable labels and tax"
      testId="admin-fee-schedules-page"
      actions={
        <Button onClick={openCreate} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="create-fee-schedule-button"><Plus className="w-4 h-4 mr-2" />New Fee Schedule</Button>
      }
    >
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Organization</TableHead><TableHead>AI Rate</TableHead>
            <TableHead>Tier 1</TableHead><TableHead>Tier 2</TableHead><TableHead>Tier 3</TableHead>
            <TableHead>Tax</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {schedules.map(s => (
              <TableRow key={s.id} data-testid={`fee-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  {s.org_id ? <Badge variant="outline" className="text-xs">{orgName(s.org_id)}</Badge> : <span className="text-xs text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="font-mono text-sm">${(s.ai_rate_cents_per_minute / 100).toFixed(2)}/min</TableCell>
                <TableCell>
                  <div><span className="font-mono text-sm">${(s.human_tier1_rate_cents / 100).toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({s.human_tier1_label})</span></div>
                </TableCell>
                <TableCell>
                  <div><span className="font-mono text-sm">${(s.human_tier2_rate_cents / 100).toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({s.human_tier2_label})</span></div>
                </TableCell>
                <TableCell>
                  <div><span className="font-mono text-sm">${(s.human_tier3_rate_cents / 100).toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground ml-1">({s.human_tier3_label})</span></div>
                </TableCell>
                <TableCell>{s.tax_enabled ? <Badge className="text-xs">{s.tax_label} {s.tax_rate_percent}%</Badge> : <span className="text-xs text-muted-foreground">None</span>}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(s)} data-testid={`edit-fee-${s.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(s.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {schedules.length === 0 && <EmptyRow colSpan={8} message="No fee schedules. Create one or seed demo data." />}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="fee-schedule-dialog">
          <DialogHeader><DialogTitle>{editId ? 'Edit Fee Schedule' : 'Create Fee Schedule'}</DialogTitle>
            <DialogDescription>Configure billing rates with custom labels and tax settings</DialogDescription></DialogHeader>
          <div className="space-y-6 py-2">
            {/* Basic */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Schedule Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="fee-name" placeholder="Standard Rates" /></div>
              <div className="space-y-2">
                <Label>Organization *</Label>
                <Select value={form.org_id || '_none'} onValueChange={v => setForm(p => ({ ...p, org_id: v === '_none' ? '' : v }))}>
                  <SelectTrigger data-testid="fee-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">No organization</SelectItem>
                    {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tax */}
            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between"><Label className="font-medium">Tax</Label>
                <Switch checked={form.tax_enabled} onCheckedChange={v => setForm(p => ({ ...p, tax_enabled: v }))} data-testid="fee-tax-toggle" /></div>
              {form.tax_enabled && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Tax Label</Label><Input value={form.tax_label} onChange={e => setForm(p => ({ ...p, tax_label: e.target.value }))} data-testid="fee-tax-label" /></div>
                  <div className="space-y-1"><Label className="text-xs">Rate (%)</Label><Input type="number" value={form.tax_rate_percent} onChange={e => setForm(p => ({ ...p, tax_rate_percent: parseFloat(e.target.value) || 0 }))} data-testid="fee-tax-rate" /></div>
                </div>
              )}
            </div>

            {/* AI Rate */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>AI Rate Label</Label><Input value={form.ai_rate_label} onChange={e => setForm(p => ({ ...p, ai_rate_label: e.target.value }))} /></div>
              <div className="space-y-2"><Label>AI Rate (cents/min)</Label><Input type="number" value={form.ai_rate_cents_per_minute} onChange={e => setForm(p => ({ ...p, ai_rate_cents_per_minute: parseInt(e.target.value) || 0 }))} data-testid="fee-ai-rate" /></div>
            </div>

            {/* Tiers */}
            {[1, 2, 3].map(tier => (
              <div key={tier} className="space-y-3 rounded-lg border p-4">
                <p className="text-sm font-semibold">Tier {tier}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Label</Label><Input value={form[`human_tier${tier}_label`]} onChange={e => setForm(p => ({ ...p, [`human_tier${tier}_label`]: e.target.value }))} /></div>
                  <div className="space-y-1"><Label className="text-xs">Rate (cents/min)</Label><Input type="number" value={form[`human_tier${tier}_rate_cents`]} onChange={e => setForm(p => ({ ...p, [`human_tier${tier}_rate_cents`]: parseInt(e.target.value) || 0 }))} data-testid={`fee-tier${tier}-rate`} /></div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Languages</Label>
                  <Popover>
                    <PopoverTrigger asChild><Button variant="outline" size="sm" className="w-full justify-start text-xs" data-testid={`fee-tier${tier}-langs`}>{form[`human_tier${tier}_languages`].length ? `${form[`human_tier${tier}_languages`].length} selected` : 'Select languages'}</Button></PopoverTrigger>
                    <PopoverContent className="w-60 p-2" align="start">
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {languages.map(l => (
                          <label key={l.code} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-accent cursor-pointer text-xs">
                            <Checkbox checked={form[`human_tier${tier}_languages`].includes(l.code)} onCheckedChange={() => toggleLang(tier, l.code)} />
                            {l.name}
                          </label>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {form[`human_tier${tier}_languages`].length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {form[`human_tier${tier}_languages`].map(code => <Badge key={code} variant="secondary" className="text-[10px]">{langName(code)}</Badge>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={!form.name} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="submit-fee-schedule">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
